import React, { useEffect } from 'react';
import { X, Check, Clock, Circle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';
interface ApplicationStatusPanelProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ApplicationStatusPanel({
  isOpen,
  onClose
}: ApplicationStatusPanelProps) {
  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex justify-end" data-id="element-3">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 transition-opacity animate-in fade-in duration-300" onClick={onClose} aria-hidden="true" data-id="element-4" />

      {/* Slide-over Panel */}
      <div className="relative w-full max-w-md bg-white shadow-xl h-full flex flex-col animate-in slide-in-from-right duration-300" data-id="element-5">
        {/* Header */}
        <div className="px-6 py-6 border-b border-gray-100 flex-shrink-0" data-id="element-6">
          <div className="flex items-start justify-between mb-3" data-id="element-7">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-8">
              Application Status
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100" aria-label="Close panel" data-id="element-9">
              <X className="w-6 h-6" data-id="element-10" />
            </button>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900" data-id="element-11">
            Under Review
          </span>
        </div>

        {/* Content (Timeline) */}
        <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-12">
          <div className="relative" data-id="element-13">
            {/* Connecting Line */}
            <div className="absolute left-[15px] top-4 bottom-8 w-0.5 bg-gray-200" aria-hidden="true" data-id="element-14" />

            <div className="space-y-8 relative" data-id="element-15">
              {/* Step 1: Completed */}
              <div className="flex gap-4 relative" data-id="element-16">
                <div className="relative z-10 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 ring-4 ring-white" data-id="element-17">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-18" />
                </div>
                <div className="pt-1" data-id="element-19">
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-20">
                    Application Submitted
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-21">
                    05 May 2026, 04:32 PM
                  </p>
                </div>
              </div>

              {/* Step 2: Action Required (Completed) */}
              <div className="flex gap-4 relative" data-id="element-22">
                <div className="relative z-10 w-8 h-8 rounded-full bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0 ring-4 ring-white" data-id="element-23">
                  <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-24" />
                </div>
                <div className="pt-1" data-id="element-25">
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-26">
                    Action Required
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-27">
                    06 May 2026, 09:15 AM
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-28">
                    Bank requested updates to Supporting Documents.
                  </p>
                </div>
              </div>

              {/* Step 3: Resubmitted (Completed) */}
              <div className="flex gap-4 relative" data-id="element-29">
                <div className="relative z-10 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 ring-4 ring-white" data-id="element-30">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-31" />
                </div>
                <div className="pt-1" data-id="element-32">
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-33">
                    Application Resubmitted
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-34">
                    06 May 2026, 11:45 AM
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-35">
                    Updates provided.
                  </p>
                </div>
              </div>

              {/* Step 4: Active */}
              <div className="flex gap-4 relative" data-id="element-36">
                <div className="relative z-10 w-8 h-8 rounded-full bg-[#FFCC00] flex items-center justify-center flex-shrink-0 ring-4 ring-white" data-id="element-37">
                  <Clock className="w-4 h-4 text-gray-900" data-id="element-38" />
                </div>
                <div className="pt-1" data-id="element-39">
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-40">
                    Bank Admin Review
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-41">
                    In progress. Our team is reviewing your updated documents.
                    This typically takes 1-2 business days.
                  </p>
                </div>
              </div>

              {/* Step 5: Pending */}
              <div className="flex gap-4 relative" data-id="element-42">
                <div className="relative z-10 w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center flex-shrink-0 ring-4 ring-white" data-id="element-43">
                  <Circle className="w-3 h-3 text-transparent" data-id="element-44" />
                </div>
                <div className="pt-1" data-id="element-45">
                  <h3 className="text-sm font-bold text-gray-400" data-id="element-46">
                    Account Approved
                  </h3>
                  <p className="text-sm text-gray-400 mt-1" data-id="element-47">
                    Pending review completion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0" data-id="element-48">
          <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-49">
            Need to update a document? If our team requires more information, we
            will notify you via email and unlock the required sections in your
            hub.
          </p>
          <div className="flex justify-end" data-id="element-50">
            <Button variant="outline" data-id="element-51">Contact Support</Button>
          </div>
        </div>
      </div>
    </div>;
}